import type { Order } from '@seen/core/models/orders/Order'
import {
  cn,
  IconHexagon,
  IconHexagonCheck,
  IconHexagonDotted,
  IconHexagonFilled15,
  IconHexagonFilled50,
  IconHexagonFilled85,
  IconHexagonX,
} from '@seen/ui'
import type { JSX } from 'react'
import { match } from 'ts-pattern'

export interface IconOrderStatusProps {
  status: Order['status'] | undefined
  className?: string
  /**
   * When true, pulses the icon's opacity to indicate an in-flight async
   * submission to the lab vendor. Backed by the `submissionState` tag —
   * survives page refresh because state lives on the resource.
   */
  isSubmitting?: boolean
}

export function IconOrderStatus({
  status,
  className,
  isSubmitting,
}: IconOrderStatusProps): JSX.Element {
  const finalClassName = cn(className, isSubmitting && 'animate-pulse')
  return match(status)
    .with(undefined, () => <IconHexagon className={finalClassName} />)
    .with('draft', () => <IconHexagonDotted className={finalClassName} />)
    .with('on-hold', () => <IconHexagon className={finalClassName} />)
    .with('active', () => <IconHexagonFilled15 className={finalClassName} />)
    .with('coordinated', () => (
      <IconHexagonFilled50 className={finalClassName} strokeColor="#baba00" />
    ))
    .with('effectuated', () => (
      <IconHexagonFilled85 className={finalClassName} strokeColor="#75ad15" />
    ))
    .with('completed', () => <IconHexagonCheck className={finalClassName} />)
    .with('revoked', () => <IconHexagonX className={finalClassName} />)
    .with('entered-in-error', () => <IconHexagonX className={finalClassName} />)
    .exhaustive()
}
